'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useAuth, useWallets } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { ArrowDataTransferHorizontalIcon, Download01Icon, PlugSocketIcon, PlusSignIcon, SentIcon } from '@/components/ui/icon/glyphs';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { ArchiveWallet } from '@/components/wallet/archive-wallet';
import { ArchivedWalletsSection } from '@/components/wallet/archived-wallets-section';
import { ConnectWallet } from '@/components/wallet/connect-wallet';
import { CreateWallet } from '@/components/wallet/create-wallet';
import { ImportWallet } from '@/components/wallet/import-wallet';
import { MoveFunds } from '@/components/wallet/move-funds';
import { TransferFunds } from '@/components/wallet/transfer-funds';
import { UnlinkWallet } from '@/components/wallet/unlink-wallet';
import { WalletList } from '@/components/wallet/wallet-list';
import { TOAST_IDS } from '@/config/toast-ids';
import { useNotifyWalletSwitched } from '@/hooks/wallet/use-notify-wallet-switched';
import { showToast } from '@/lib/ui/toast';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { useLayoutStore } from '@/stores/layout';
import type { Wallet } from '@/types/auth';

/**
 * Modal to manage wallets: list, create, connect (import external), archive.
 * Opened from header wallet dropdown, mobile menu, and settings dropdown.
 */
// Exactly one sub-dialog is open at a time (each is a modal overlay), so a single
// discriminated state models them better than seven parallel flags.
type ActiveModal = {
  kind: 'create';
} | {
  kind: 'connect';
} | {
  kind: 'import';
} | {
  kind: 'transfer';
} | {
  kind: 'move';
} | {
  kind: 'archive';
  wallet: Wallet;
} | {
  kind: 'disconnect';
  wallet: Wallet;
};
interface ManageWalletsModalContentProps {
  feature: ReturnType<typeof useMultiWalletFeature>;
}
export function ManageWalletsModal() {
  const $ = _c(2);
  const feature = useMultiWalletFeature();
  let t0;
  if ($[0] !== feature) {
    t0 = <ManageWalletsModalContent key={feature.state} feature={feature} />;
    $[0] = feature;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function ManageWalletsModalContent(t0) {
  const $ = _c(90);
  const {
    feature
  } = t0;
  const t = useTranslations("wallet");
  const open = useLayoutStore(_temp);
  const setOpen = useLayoutStore(_temp2);
  const {
    isAuthenticated,
    isLoading: authLoading
  } = useAuth();
  const {
    wallets,
    isLoading,
    createWallet,
    importWallet,
    exportWallet,
    archiveWallet,
    unlinkWallet,
    renameWallet
  } = useWallets();
  const {
    activeWalletId,
    setActiveWalletId
  } = useActiveWallet();
  const {
    enabled: multiWalletsEnabled,
    primaryWallet,
    canStartOperation
  } = feature;
  const notifyWalletSwitched = useNotifyWalletSwitched();
  const [activeModal, setActiveModal] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setActiveModal(null);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const closeModal = t1;
  let t2;
  if ($[1] !== canStartOperation) {
    t2 = function openMultiWalletModal(modal, operation) {
      if (!canStartOperation(operation, "wallet_management")) {
        return;
      }
      setActiveModal(modal);
    };
    $[1] = canStartOperation;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const openMultiWalletModal = t2;
  let t3;
  if ($[3] !== setOpen) {
    t3 = function handleOpenChange(next) {
      setOpen(next);
      if (!next) {
        setActiveModal(null);
      }
    };
    $[3] = setOpen;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleOpenChange = t3;
  if (!open) {
    return null;
  }
  const isReady = !authLoading && !isLoading && isAuthenticated;
  let t4;
  if ($[5] !== multiWalletsEnabled || $[6] !== primaryWallet || $[7] !== wallets) {
    t4 = multiWalletsEnabled ? wallets : primaryWallet === null ? [] : [primaryWallet];
    $[5] = multiWalletsEnabled;
    $[6] = primaryWallet;
    $[7] = wallets;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const visibleWallets = t4;
  let t5;
  if ($[9] !== multiWalletsEnabled || $[10] !== visibleWallets) {
    t5 = multiWalletsEnabled && visibleWallets.filter(_temp3).length > 1;
    $[9] = multiWalletsEnabled;
    $[10] = visibleWallets;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const hasMultipleActiveWallets = t5;
  let t6;
  if ($[12] !== t) {
    t6 = t("picker.title");
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== hasMultipleActiveWallets || $[15] !== isReady || $[16] !== multiWalletsEnabled || $[17] !== openMultiWalletModal || $[18] !== t) {
    t7 = isReady ? <ModalFooter wrap={true}><AnimatedButton variant="secondary" onClick={() => setActiveModal({
        kind: "transfer"
      })}><Icon icon={SentIcon} className="size-4" aria-hidden={true} />{t("transfer.send_button")}</AnimatedButton>{hasMultipleActiveWallets && <AnimatedButton variant="secondary" onClick={() => openMultiWalletModal({
        kind: "move"
      }, "move_funds")}><Icon icon={ArrowDataTransferHorizontalIcon} className="size-4" aria-hidden={true} />{t("move.move_button")}</AnimatedButton>}{multiWalletsEnabled ? <><AnimatedButton variant="secondary" suffix={PlusSignIcon} onClick={() => openMultiWalletModal({
          kind: "create"
        }, "create")}>{t("picker.create_button")}</AnimatedButton><AnimatedButton variant="secondary" suffix={PlugSocketIcon} onClick={() => openMultiWalletModal({
          kind: "connect"
        }, "external_connect")}>{t("picker.connect_button")}</AnimatedButton><AnimatedButton variant="secondary" suffix={Download01Icon} onClick={() => openMultiWalletModal({
          kind: "import"
        }, "import")}>{t("picker.import_button")}</AnimatedButton></> : null}</ModalFooter> : undefined;
    $[14] = hasMultipleActiveWallets;
    $[15] = isReady;
    $[16] = multiWalletsEnabled;
    $[17] = openMultiWalletModal;
    $[18] = t;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== activeWalletId || $[21] !== authLoading || $[22] !== canStartOperation || $[23] !== exportWallet || $[24] !== isAuthenticated || $[25] !== isLoading || $[26] !== multiWalletsEnabled || $[27] !== notifyWalletSwitched || $[28] !== openMultiWalletModal || $[29] !== renameWallet || $[30] !== setActiveWalletId || $[31] !== setOpen || $[32] !== t || $[33] !== visibleWallets || $[34] !== wallets) {
    t8 = authLoading || isLoading ? <div className="min-h-30 animate-pulse rounded-control bg-background-tertiary" /> : !isAuthenticated ? <p className="py-4 text-body text-foreground-secondary">{t("picker.empty")}</p> : <div className="flex flex-col gap-4"><WalletList wallets={visibleWallets as import('@/types/auth').Wallet[]} activeWalletId={activeWalletId} rowClassName="bg-background-primary" onSelect={walletId => {
        if (walletId === activeWalletId) {
          return;
        }
        if (!canStartOperation("switch", "wallet_management")) {
          return;
        }
        const target = wallets.find(w_0 => w_0.id === walletId);
        if (target?.isAvailable !== true) {
          return;
        }
        setActiveWalletId(walletId);
        notifyWalletSwitched({
          fromWalletId: activeWalletId,
          toWalletId: target.id,
          label: target.label
        });
      }} onRename={async params => {
        showToast({
          id: TOAST_IDS.WALLET_RENAMED,
          message: t("rename.renaming"),
          variant: "loading"
        });
        await renameWallet(params);
        showToast({
          id: TOAST_IDS.WALLET_RENAMED,
          message: t("rename.success"),
          variant: "success"
        });
      }} onArchive={wallet => {
        openMultiWalletModal({
          kind: "archive",
          wallet
        }, "archive");
      }} onExport={wallet_0 => {
        setOpen(false);
        exportWallet({
          address: wallet_0.address,
          walletType: wallet_0.type
        });
      }} onDisconnect={wallet_1 => {
        openMultiWalletModal({
          kind: "disconnect",
          wallet: wallet_1
        }, "external_disconnect");
      }} />{multiWalletsEnabled ? <ArchivedWalletsSection /> : null}</div>;
    $[20] = activeWalletId;
    $[21] = authLoading;
    $[22] = canStartOperation;
    $[23] = exportWallet;
    $[24] = isAuthenticated;
    $[25] = isLoading;
    $[26] = multiWalletsEnabled;
    $[27] = notifyWalletSwitched;
    $[28] = openMultiWalletModal;
    $[29] = renameWallet;
    $[30] = setActiveWalletId;
    $[31] = setOpen;
    $[32] = t;
    $[33] = visibleWallets;
    $[34] = wallets;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== handleOpenChange || $[37] !== open || $[38] !== t6 || $[39] !== t7 || $[40] !== t8) {
    t9 = <SilkSheet open={open} onOpenChange={handleOpenChange} variant="detached" size="lg" title={t6} footer={t7}>{t8}</SilkSheet>;
    $[36] = handleOpenChange;
    $[37] = open;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  const t10 = multiWalletsEnabled && activeModal?.kind === "create";
  let t11;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = isOpen => {
      if (!isOpen) {
        closeModal();
      }
    };
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] !== canStartOperation || $[44] !== createWallet) {
    t12 = async label => {
      if (!canStartOperation("create", "wallet_management")) {
        return;
      }
      await createWallet({
        label
      });
    };
    $[43] = canStartOperation;
    $[44] = createWallet;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  let t13;
  if ($[46] !== t10 || $[47] !== t12) {
    t13 = <CreateWallet open={t10} onOpenChange={t11} onCreateWallet={t12} />;
    $[46] = t10;
    $[47] = t12;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  const t14 = multiWalletsEnabled && activeModal?.kind === "connect";
  let t15;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = isOpen_0 => {
      if (!isOpen_0) {
        closeModal();
      }
    };
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== setOpen) {
    t16 = () => setOpen(false);
    $[50] = setOpen;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== t14 || $[53] !== t16) {
    t17 = <ConnectWallet open={t14} onOpenChange={t15} onBeforeTurnkeyModal={t16} />;
    $[52] = t14;
    $[53] = t16;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  const t18 = multiWalletsEnabled && activeModal?.kind === "import";
  let t19;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = isOpen_1 => {
      if (!isOpen_1) {
        closeModal();
      }
    };
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== canStartOperation || $[57] !== importWallet) {
    t20 = async params_0 => {
      if (!canStartOperation("import", "wallet_management")) {
        return null;
      }
      return importWallet(params_0);
    };
    $[56] = canStartOperation;
    $[57] = importWallet;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== setOpen) {
    t21 = () => setOpen(false);
    $[59] = setOpen;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== t18 || $[62] !== t20 || $[63] !== t21) {
    t22 = <ImportWallet open={t18} onOpenChange={t19} onImportWallet={t20} onBeforeTurnkeyModal={t21} />;
    $[61] = t18;
    $[62] = t20;
    $[63] = t21;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  const t23 = activeModal?.kind === "transfer";
  let t24;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = isOpen_2 => {
      if (!isOpen_2) {
        closeModal();
      }
    };
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== t23) {
    t25 = <TransferFunds open={t23} onOpenChange={t24} />;
    $[66] = t23;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== activeModal?.kind || $[69] !== multiWalletsEnabled) {
    t26 = multiWalletsEnabled ? <MoveFunds open={activeModal?.kind === "move"} onOpenChange={isOpen_3 => {
      if (!isOpen_3) {
        closeModal();
      }
    }} /> : null;
    $[68] = activeModal?.kind;
    $[69] = multiWalletsEnabled;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== activeModal || $[72] !== archiveWallet || $[73] !== canStartOperation || $[74] !== multiWalletsEnabled) {
    t27 = multiWalletsEnabled && activeModal?.kind === "archive" ? <ArchiveWallet open={true} onOpenChange={isOpen_4 => {
      if (!isOpen_4) {
        closeModal();
      }
    }} walletLabel={activeModal.wallet.label} onArchive={async () => {
      if (!canStartOperation("archive", "wallet_management")) {
        return;
      }
      await archiveWallet(activeModal.wallet.id);
      closeModal();
    }} /> : null;
    $[71] = activeModal;
    $[72] = archiveWallet;
    $[73] = canStartOperation;
    $[74] = multiWalletsEnabled;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  let t28;
  if ($[76] !== activeModal || $[77] !== canStartOperation || $[78] !== multiWalletsEnabled || $[79] !== unlinkWallet) {
    t28 = multiWalletsEnabled && activeModal?.kind === "disconnect" ? <UnlinkWallet open={true} onOpenChange={isOpen_5 => {
      if (!isOpen_5) {
        closeModal();
      }
    }} walletLabel={activeModal.wallet.label} walletType={activeModal.wallet.type} onUnlink={async () => {
      if (!canStartOperation("external_disconnect", "wallet_management")) {
        return;
      }
      await unlinkWallet(activeModal.wallet.id);
      closeModal();
    }} /> : null;
    $[76] = activeModal;
    $[77] = canStartOperation;
    $[78] = multiWalletsEnabled;
    $[79] = unlinkWallet;
    $[80] = t28;
  } else {
    t28 = $[80];
  }
  let t29;
  if ($[81] !== t13 || $[82] !== t17 || $[83] !== t22 || $[84] !== t25 || $[85] !== t26 || $[86] !== t27 || $[87] !== t28 || $[88] !== t9) {
    t29 = <>{t9}{t13}{t17}{t22}{t25}{t26}{t27}{t28}</>;
    $[81] = t13;
    $[82] = t17;
    $[83] = t22;
    $[84] = t25;
    $[85] = t26;
    $[86] = t27;
    $[87] = t28;
    $[88] = t9;
    $[89] = t29;
  } else {
    t29 = $[89];
  }
  return t29;
}
function _temp3(w) {
  return w.archivedAt === null;
}
function _temp2(s_0) {
  return s_0.setManageWalletsOpen;
}
function _temp(s) {
  return s.manageWalletsOpen;
}